<template>
    <DatePicker
        v-bind="primeVueProps"
        :model-value="normalizedModelValue"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <template v-if="events && events.length > 0" #date="slotProps">
            <span :class="getDateClasses(slotProps.date)">
                {{ slotProps.date.day }}
                <span v-if="hasEvent(slotProps.date)" class="e-datetimepicker-indicator" :class="indicatorClass" />
            </span>
        </template>
        <template v-for="(_, slot) in $slots" #[slot]="scope">
            <slot :name="slot" v-bind="scope" />
        </template>
    </DatePicker>
</template>

<script>
import { computed } from 'vue';

export default {
    name: 'EDatetimepicker',
    inheritAttrs: false,
    props: {
        modelValue: {
            type: [Date, String],
            default: null,
        },
        minDatetime: {
            type: Date,
            default: null,
        },
        maxDatetime: {
            type: Date,
            default: null,
        },
        timepicker: {
            type: Object,
            default: () => ({}),
        },
        datepicker: {
            type: Object,
            default: () => ({}),
        },
        placeholder: {
            type: String,
            default: undefined,
        },
        editable: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        icon: {
            type: String,
            default: undefined,
        },
        position: {
            type: String,
            default: undefined,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: undefined,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        openOnFocus: {
            type: Boolean,
            default: false,
        },
        tzOffset: {
            type: Number,
            default: 0,
        },
        locale: {
            type: [String, Object],
            default: undefined,
        },
        events: {
            type: Array,
            default: null,
        },
        indicators: {
            type: String,
            default: 'dots',
        },
    },
    emits: ['update:modelValue'],
    setup(props, { attrs }) {
        const normalizedModelValue = computed(() => {
            if (!props.modelValue) return null;
            if (props.modelValue instanceof Date) return props.modelValue;
            if (typeof props.modelValue === 'string') {
                const parsed = new Date(props.modelValue);
                return isNaN(parsed.getTime()) ? null : parsed;
            }
            return null;
        });

        const primeVueProps = computed(() => {
            const pvProps = {
                ...attrs,
                showTime: true,
                timeOnly: false,
                minDate: props.minDatetime,
                maxDate: props.maxDatetime,
                hourFormat: props.timepicker?.hourFormat === '24' ? '24' : '12',
                showSeconds: props.timepicker?.enableSeconds || false,
                stepMinute: props.timepicker?.incrementMinutes || 1,
                placeholder: props.placeholder,
                manualInput: props.editable,
                showClear: props.clearable,
                showIcon: !!props.icon,
                iconDisplay: props.icon ? 'input' : undefined,
                fluid: props.expanded,
                disabled: props.disabled,
                inline: props.inline,
                loading: props.loading,
                required: props.required,
                autoFocus: props.openOnFocus,
            };

            if (props.size === 'is-small') {
                pvProps.size = 'small';
            } else if (props.size === 'is-large') {
                pvProps.size = 'large';
            }

            return pvProps;
        });

        const indicatorClass = computed(() => {
            if (!props.indicators) return '';
            return `e-datetimepicker-indicator--${props.indicators}`;
        });

        const normalizeDate = date => {
            if (!date) return null;
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            return d.getTime();
        };

        const hasEvent = dateObj => {
            if (!props.events || props.events.length === 0) return false;

            // PrimeVue passes {day, month, year} object, not a Date
            let targetDate;
            if (dateObj.day !== undefined && dateObj.month !== undefined && dateObj.year !== undefined) {
                // Note: month is 0-indexed in PrimeVue
                targetDate = new Date(dateObj.year, dateObj.month, dateObj.day);
            } else {
                targetDate = new Date(dateObj);
            }

            const targetTime = normalizeDate(targetDate);
            return props.events.some(event => {
                const eventTime = normalizeDate(event.date || event);
                return eventTime === targetTime;
            });
        };

        const getDateClasses = dateObj => {
            return {
                'e-datetimepicker-date-with-event': hasEvent(dateObj),
            };
        };

        return {
            normalizedModelValue,
            primeVueProps,
            indicatorClass,
            hasEvent,
            getDateClasses,
        };
    },
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.e-datetimepicker-date-with-event {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.e-datetimepicker-indicator {
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
}

.e-datetimepicker-indicator--dots {
    @apply tw:w-1 tw:h-1 tw:rounded-full tw:bg-primary;
}

.e-datetimepicker-indicator--bars {
    @apply tw:w-4/5 tw:h-0.5 tw:bg-primary tw:rounded-sm;
}
</style>
